<template>
  <div class="gamification-condition-quiz-rank-item resource-prerequisite-item">
    <div class="section-prerequisite-dropdown-container">
      <dropdown-component
        class="section-prerequisite-dropdown prerequisite-dropdown"
        :placeholder="t('drop_down_item_select_selection_label')"
        :invalid-message="validationErrors.section"
        :value="sectionId"
        :disabled="disableFields"
        @change="handleChangeSection"
      >
        <template v-if="sectionSelectedObject" #internal-caption>
          <label
            class="section-label"
            :data-icon-text="sectionIconText(sectionSelectedObject.pageNo)"
          >
            <SectionIcon />
          </label>

          <ellipsis-text :text="sectionSelectedObject.title" />
        </template>

        <cv-dropdown-item
          v-for="section in sections"
          :key="section.id"
          :value="section.id.toString()"
        >
          <label class="section-label" :data-icon-text="sectionIconText(section.pageNo)">
            <SectionIcon />
          </label>

          <ellipsis-text :text="section.title" />
        </cv-dropdown-item>
      </dropdown-component>
    </div>
    <dropdown-component
      class="quiz-rank-activity-prerequisite-dropdown prerequisite-dropdown"
      :placeholder="t('drop_down_item_select_quiz_label')"
      :disabled="sectionId === null || disableFields"
      :invalid-message="validationErrors.activity"
      :value="activityId"
      @change="handleChangeActivity"
    >
      <template v-if="activitySelectedObject" #internal-caption>
        <label class="activity-label" :data-icon-text="activitySelectedObject.pageNo">
          <QuizPageIcon />
        </label>

        <ellipsis-text :text="activitySelectedObject.title" />
      </template>

      <cv-dropdown-item
        v-for="quizFromSection in quizzesFromSection(sectionId)"
        :key="quizFromSection.id"
        :value="quizFromSection.id.toString()"
      >
        <label class="activity-label" :data-icon-text="quizFromSection.pageNo">
          <QuizPageIcon />
        </label>

        <ellipsis-text :text="quizFromSection.title" />
      </cv-dropdown-item>
    </dropdown-component>

    <div class="criterion-selection">
      <div>{{ t("quiz_rank_position_from_label") }}</div>
      <cv-number-input
        min="1"
        max="100"
        :model-value="minRankPosition"
        :disabled="sectionId === null || activityId === null || disableFields"
        @keydown.enter.prevent
        @change="handleChangeMinRankPosition"
        @input="handleInputMinRankPosition"
      />
      <div>{{ t("to_small_letter_case") }}</div>
      <cv-number-input
        min="1"
        max="100"
        :model-value="maxRankPosition"
        :disabled="sectionId === null || activityId === null || disableFields"
        @keydown.enter.prevent
        @change="handleChangeMaxRankPosition"
        @input="handleInputMaxRankPosition"
      />
    </div>

    <cv-button kind="ghost" class="icon-button btn-delete-prerequisite">
      <svg-icon name="Trash24" kind="action" class="delete-prerequisite" @click="deleteCondition" />
    </cv-button>
  </div>
</template>

<script setup lang="ts">
import ValidationMixin from "@utils/mixin/validation.mixin";
import SectionIcon from "@images/icons/indicative/32px/Section32.svg?component";
import QuizPageIcon from "@images/icons/indicative/32px/QuizPage32.svg?component";
import StringUtils from "@utils/string.utils";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant";
import {
  DEFAULT_MIN_RANK,
  DEFAULT_MAX_RANK,
} from "@modules/rule-condition/service/rule.condition.constant";
import { ref, computed, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm.ts";

defineOptions({
  mixins: [ValidationMixin],
});

const { vm } = useVm();
const { t } = useI18n();
const emit = defineEmits(["change", "delete"]);

// Props
interface Props {
  sections: unknown[];
  condition: Record<string, unknown>;
  disableFields?: boolean;
  validationErrors?: Record<string, unknown>;
}

const { sections, condition, disableFields = false, validationErrors = {} } = defineProps<Props>();

// Data
const sectionId = ref(condition.sectionId);
const activityId = ref(condition.activityId);
const minRankPosition = ref(condition.minRankPosition);
const previousMinRankPosition = ref(condition.minRankPosition);
const maxRankPosition = ref(condition.maxRankPosition);
const previousMaxRankPosition = ref(condition.maxRankPosition);

// Computed
const quizzesFromSection = computed(() => {
  return (sectionId) => {
    const section = sections.find((section) => section.id === parseInt(sectionId));
    if (!section) {
      return [];
    }
    return section.activityList.filter(
      (sectionActivity) => sectionActivity.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ,
    );
  };
});

const sectionIconText = computed(() => {
  return (sectionPageNo) => {
    return sectionPageNo ? `${StringUtils.getAlphabeticalLetter(sectionPageNo - 1)}` : "";
  };
});

const sectionSelectedObject = computed(() => {
  return sections.find((item) => item.id.toString() === sectionId.value?.toString());
});

const activitySelectedObject = computed(() => {
  return quizzesFromSection
    .value(sectionId.value)
    .find((item) => item.id.toString() === activityId.value?.toString());
});

// Methods
const deleteCondition = function () {
  emit("delete", condition.id);
};

const handleChangeSection = function (value) {
  sectionId.value = value;
  activityId.value = null;
  handleEmitChangeEvent();
  resetMinAndMaxRankToDefault();
};

const handleChangeActivity = function (value) {
  if (value === null) return;
  activityId.value = value;
  handleEmitChangeEvent();
  resetMinAndMaxRankToDefault();
};

const resetMinAndMaxRankToDefault = function () {
  minRankPosition.value = DEFAULT_MIN_RANK;
  maxRankPosition.value = DEFAULT_MAX_RANK;
};

const handleChangeMinRankPosition = function (event) {
  const value = event.target.valueAsNumber;
  if (!validateMinRankPosition(value)) {
    minRankPosition.value = -1;
    nextTick(() => {
      minRankPosition.value = previousMinRankPosition.value;
    });
    return;
  }
  minRankPosition.value = value;
  previousMinRankPosition.value = value;
  handleEmitChangeEvent();
};

const handleInputMinRankPosition = function (value) {
  if (!validateMinRankPosition(value)) {
    if (value === maxRankPosition.value + 1) {
      minRankPosition.value = -1;
      nextTick(() => {
        minRankPosition.value = previousMinRankPosition.value;
      });
    }
    return;
  }
  minRankPosition.value = value;
  previousMinRankPosition.value = value;
  handleEmitChangeEvent();
};

const validateMinRankPosition = function (value) {
  return vm.validateNumberInput(value, { min: 1, max: 100 }) && value <= maxRankPosition.value;
};

const handleChangeMaxRankPosition = function (event) {
  const value = event.target.valueAsNumber;
  if (!validateMaxRankPosition(value)) {
    maxRankPosition.value = -1;
    nextTick(() => {
      maxRankPosition.value = previousMaxRankPosition.value;
    });
    return;
  }
  maxRankPosition.value = value;
  previousMaxRankPosition.value = value;
  handleEmitChangeEvent();
};

const handleInputMaxRankPosition = function (value) {
  if (!validateMaxRankPosition(value)) {
    if (value < minRankPosition.value) {
      maxRankPosition.value = -1;
      nextTick(() => {
        maxRankPosition.value = previousMaxRankPosition.value;
      });
    }
    return;
  }
  maxRankPosition.value = value;
  previousMaxRankPosition.value = value;
  handleEmitChangeEvent();
};

const validateMaxRankPosition = function (value) {
  return vm.validateNumberInput(value, { min: 1, max: 100 }) && value >= minRankPosition.value;
};

const handleEmitChangeEvent = function () {
  emit("change", {
    targetId: condition.id,
    sectionId: parseInt(sectionId.value),
    activityId: parseInt(activityId.value),
    minRankPosition: minRankPosition.value,
    maxRankPosition: maxRankPosition.value,
  });
};
</script>
